"use client"

import { Copy } from "@/app/components/copy/copy"
import { ChevronIcon } from "@/app/icons/chevron"
import { trackProductEvent } from "@/app/infra/product-events"
import { isGachaEvmDelivered } from "@anaconda/core/gacha/evm-purchase"
import { Trans, useLingui } from "@lingui/react/macro"
import {
  useConnectWallet,
  useLogin,
  useModalStatus,
  usePrivy,
} from "@privy-io/react-auth"
import type { ConnectedStandardSolanaWallet } from "@privy-io/react-auth/solana"
import {
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  sonnerToast,
} from "@rarible_int/anaconda_ui_components"
import { atom, wrap } from "@reatom/core"
import { reatomComponent, useWrap } from "@reatom/react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GachaAuthenticityLabel } from "./gacha-authenticity-label"
import { GachaBuybackConfirmationBody } from "./gacha-buyback-confirmation-body"
import { formatBuybackRatePercent } from "./gacha-buyback-rate"
import {
  GachaBuybackResultActions,
  GachaBuybackResultSummary,
} from "./gacha-buyback-result-actions"
import { gachaCtaButtonRowPrimaryClassName } from "./gacha-cta-button-styles"
import {
  type GachaMachineView,
  formatUsd,
  isWatchGachaMachine,
} from "./gacha-data"
import { GachaDialogContent } from "./gacha-dialog-content"
import {
  GachaEthPaymentRecoveryDialog,
  GachaEthPaymentReview,
} from "./gacha-eth-payment-review"
import { GachaEthPaymentRecoveryError } from "./gacha-eth-payment-tracking"
import { GachaEvmBuybackButton } from "./gacha-evm-buyback-button"
import { gachaEvmPurchases } from "./gacha-evm-purchase-state"
import { reconnectGachaEvmWallet } from "./gacha-evm-wallet-reconnect"
import type {
  GachaEthPaymentOffer,
  GachaPullResult,
  GachaPullResultView,
  GachaPurchaseExecution,
  GachaResultExecution,
} from "./gacha-execution"
import { GachaFaqSection } from "./gacha-faq-section"
import { GachaInstantBuybackIntroDialog } from "./gacha-instant-buyback-intro-dialog"
import { filterGachaPurchaseMachines } from "./gacha-machine-exclusions"
import { GachaMachineMedia } from "./gacha-machine-media"
import { fetchGachaMachinesStock } from "./gacha-machine-stock"
import {
  type GachaPaymentAsset,
  GachaPaymentSelector,
} from "./gacha-payment-selector"
import { GachaProgressLabel } from "./gacha-progress-label"
import {
  getGachaDisclaimerPurchaseGateAction,
  shouldOpenGachaPurchaseDialog,
  shouldUseModalGachaPurchaseDialog,
} from "./gacha-purchase-dialog"
import { GachaPurchaseDisclaimerDialog } from "./gacha-purchase-disclaimer-dialog"
import { GachaResultDialogContent } from "./gacha-result-dialog-content"
import { playGachaRevealWhoosh } from "./gacha-reveal-audio"
import { pullResultToRevealCard } from "./gacha-reveal-card"
import { GachaRevealOverlay } from "./gacha-reveal-overlay"
import { GachaSendGiftButton } from "./gacha-send-gift-button"
import {
  type GachaSeriesView,
  buildGachaSeries,
  getDefaultMachineCode,
  getMachinePackImage,
  getMachineUnavailableLabel,
  getPackAvailabilityText,
  getSortedSeriesMachines,
  isFeaturedGachaMachine,
} from "./gacha-series"
import { GachaSeriesCard } from "./gacha-series-card"
import { isGachaWalletCancellationMessage } from "./gacha-wallet-cancellation"
import { GachaZoomableCardPreview } from "./gacha-zoomable-card-preview"
import { useGachaApiOptions } from "./use-gacha-api-options"
import { useGachaDisclaimerAcceptance } from "./use-gacha-disclaimer-acceptance"
import { useGachaEvmBuybackPrice } from "./use-gacha-evm-buyback-price"
import { useGachaEvmExecution } from "./use-gacha-evm-execution"
import { useGachaInstantBuybackIntro } from "./use-gacha-instant-buyback-intro"
import { useGachaNetworkContext } from "./use-gacha-network"
import { useGachaReveal } from "./use-gacha-reveal"
import { useGachaRevealMute } from "./use-gacha-reveal-mute"
import { useGachaSolanaExecution } from "./use-gacha-solana-execution"
import { useGachaUsdcBalance } from "./use-gacha-usdc-balance"

type GachaSeriesOverviewProps = {
  machines: readonly GachaMachineView[]
}

type GachaMachineRefreshResult = {
  machines: readonly GachaMachineView[]
  selectedMachine?: GachaMachineView | null
}

const BUYBACK_WINDOW_MS = 72 * 60 * 60 * 1000

function resolveDeliveredPullResult(result: GachaPullResultView | null) {
  const record = result?.deliveryPending
    ? gachaEvmPurchases
        .records()
        .find(
          (record) =>
            record.id === result.purchaseId &&
            record.memo === result.memo &&
            record.chain.chainId === result.chainId &&
            record.chain.cardContract.toLowerCase() ===
              result.contractAddress.toLowerCase() &&
            record.walletAddress.toLowerCase() ===
              result.purchaseWalletAddress?.toLowerCase() &&
            record.evidence &&
            record.state === "delivered" &&
            record.award &&
            isGachaEvmDelivered(200, record.award, record),
        )
    : undefined
  return result && record
    ? {
        ...result,
        deliveryPending: false,
        tokenId: record.award?.evm_token_id ?? undefined,
      }
    : result
}

const GachaEvmRevealBuybackPreload = reatomComponent<{
  result: GachaPullResultView
}>(({ result }) => {
  const delivered = resolveDeliveredPullResult(result)
  if (
    !delivered?.chainId ||
    delivered.deliveryPending ||
    delivered.tokenId === undefined ||
    !delivered.purchaseWalletAddress ||
    !delivered.paymentTokenAddress ||
    delivered.boughtBackLabel
  )
    return null
  return (
    <GachaEvmRevealBuybackPrice
      identity={{
        chainId: delivered.chainId,
        contract: delivered.contractAddress,
        tokenId: delivered.tokenId,
      }}
      walletAddress={delivered.purchaseWalletAddress}
      paymentToken={delivered.paymentTokenAddress}
    />
  )
}, "GachaEvmRevealBuybackPreload")

const GachaEvmRevealBuybackPrice = reatomComponent<
  Omit<Parameters<typeof useGachaEvmBuybackPrice>[0], "enabled">
>((props) => {
  useGachaEvmBuybackPrice({ ...props, enabled: true, retryTransient: true })
  return null
}, "GachaEvmRevealBuybackPrice")

function getPurchaseErrorMessage(caught: Error | string | null, isEvm = false) {
  const message =
    caught instanceof Error ? caught.message : "Gacha purchase failed"

  if (isWalletCancellationError(caught)) {
    return "Purchase cancelled."
  }

  if (
    !isEvm &&
    message.includes("Transaction could not be submitted to the network")
  ) {
    return "Payment failed. Insufficient USDC."
  }

  return message
}

function isWalletCancellationError(caught: unknown) {
  const message =
    caught instanceof Error
      ? caught.message
      : typeof caught === "string"
        ? caught
        : ""
  return isGachaWalletCancellationMessage(message)
}

function getBuybackErrorMessage(caught: unknown) {
  const message = caught instanceof Error ? caught.message : "Buyback failed"

  if (message.includes("Transaction could not be submitted to the network")) {
    return "Buyback failed. Try again."
  }

  return message
}

function formatInstantBuybackSummary(count: number, total: number) {
  return `${count} ${count === 1 ? "pack" : "packs"} instant bought back. Payout: ${formatUsd(total)}.`
}

function formatBuybackCountdown(deadlineMs: number): string {
  const remaining = deadlineMs - Date.now()
  if (remaining <= 0) return "Expired"

  const totalSec = Math.floor(remaining / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60

  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  }

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

export const GachaSeriesOverview = reatomComponent(
  ({ machines }: GachaSeriesOverviewProps) => {
    const gachaApiOptions = useGachaApiOptions()
    const [currentMachines, setCurrentMachines] = useState(machines)
    const { network } = useGachaNetworkContext()
    const series = useMemo(
      () =>
        buildGachaSeries(filterGachaPurchaseMachines(currentMachines, network)),
      [currentMachines, network],
    )
    const [selectedSeries, setSelectedSeries] =
      useState<GachaSeriesView | null>(null)
    const [openPackDialog, setOpenPackDialog] = useState(false)

    useEffect(() => {
      setCurrentMachines(machines)
    }, [machines])

    const handleOpenPack = (item: GachaSeriesView) => {
      setSelectedSeries(item)
      setOpenPackDialog(true)
    }

    const refreshSeriesMachines = useCallback(async () => {
      const latestMachines = await wrap(
        fetchGachaMachinesStock({
          machines: currentMachines,
          apiOptions: gachaApiOptions,
        }),
      )
      const latestSeries = buildGachaSeries(latestMachines)

      setCurrentMachines(latestMachines)
      setSelectedSeries((current) =>
        current
          ? (latestSeries.find((item) => item.slug === current.slug) ?? current)
          : current,
      )

      return { latestMachines, latestSeries }
    }, [currentMachines, gachaApiOptions])

    const refreshSeries = useCallback(() => {
      void refreshSeriesMachines().catch((error) => {
        console.error("Failed to refresh gacha series data", error)
      })
    }, [refreshSeriesMachines])

    const refreshMachinesBeforePurchase = useCallback(async () => {
      const { latestSeries } = await wrap(refreshSeriesMachines())
      const refreshedSeries = selectedSeries
        ? latestSeries.find((item) => item.slug === selectedSeries.slug)
        : null

      return {
        machines: refreshedSeries?.machines ?? [],
      }
    }, [refreshSeriesMachines, selectedSeries])

    return (
      <div className="flex flex-1 flex-col gap-y-8 overflow-y-auto overflow-x-hidden pt-[1px] pb-8 md:py-4">
        <section className="flex flex-col">
          <div className="mx-auto flex w-full max-w-[1192px] flex-col gap-6 px-4">
            <div className="flex flex-col gap-2">
              <div className="font-mono text-sm text-brand">Gacha Stations</div>
              <h1 className="font-headline text-2xl leading-tight text-foreground">
                Choose a station
              </h1>
              <p className="max-w-[640px] text-sm leading-relaxed text-foreground/60">
                Browse collectible categories, explore pack tiers, and crack
                packs from your favorite stations.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {series.map((item) => (
                <GachaSeriesCard
                  key={item.slug}
                  series={item}
                  onOpenPack={() => handleOpenPack(item)}
                />
              ))}
            </div>
          </div>
        </section>

        <GachaFaqSection />

        <GachaOpenPackDialog
          series={selectedSeries}
          open={openPackDialog}
          onOpenChange={setOpenPackDialog}
          onRefreshMachinesBeforePurchase={refreshMachinesBeforePurchase}
          onPurchaseComplete={refreshSeries}
        />
      </div>
    )
  },
  "GachaSeriesOverview",
)

type GachaOpenPackDialogProps = {
  series: GachaSeriesView | null
  open: boolean
  onOpenChange: (open: boolean) => void
  initialMachineCode?: string | null
  initialQuantity?: number
  initialPaymentAsset?: GachaPaymentAsset
  onPaymentAssetChange?: (asset: GachaPaymentAsset) => void
  initialInstantBuyback?: boolean
  autoPurchase?: boolean
  onInstantBuybackChange?: (enabled: boolean) => void
  onPurchaseStateChange?: (state: {
    busy: boolean
    label: string
    dots?: string
  }) => void
  onRefreshMachinesBeforePurchase?: () => Promise<GachaMachineRefreshResult>
  onPurchaseComplete?: () => void
}

export const GachaOpenPackDialog = reatomComponent(
  (props: GachaOpenPackDialogProps) => {
    const solana = useGachaSolanaExecution()
    const evm = useGachaEvmExecution()
    const { network, environment } = useGachaNetworkContext()
    const purchaseContext = `${environment}:${network}`
    const session = useMemo(() => ({ purchaseContext }), [purchaseContext])
    const currentSession = useRef(session)
    currentSession.current = session
    // The child reports state in an effect that depends on this callback.
    // Keep it stable within a session without allowing stale sessions to report.
    const onPurchaseStateChange = useCallback<
      NonNullable<GachaOpenPackDialogProps["onPurchaseStateChange"]>
    >(
      (state) => {
        if (currentSession.current === session)
          props.onPurchaseStateChange?.(state)
      },
      [props.onPurchaseStateChange, session],
    )
    const { balanceAmount, isLoading: isBalanceLoading } = useGachaUsdcBalance(
      solana.walletAddress || undefined,
    )
    const refreshBeforePurchase = props.onRefreshMachinesBeforePurchase
    const purchaseSeries = useMemo(
      () =>
        props.series
          ? {
              ...props.series,
              machines: filterGachaPurchaseMachines(
                props.series.machines,
                network,
              ),
            }
          : null,
      [props.series, network],
    )
    return (
      <GachaPurchaseDialog
        key={purchaseContext}
        {...props}
        series={purchaseSeries?.machines.length ? purchaseSeries : null}
        onRefreshMachinesBeforePurchase={
          refreshBeforePurchase
            ? async () => {
                const refreshed = await wrap(refreshBeforePurchase())
                const machines = filterGachaPurchaseMachines(
                  refreshed.machines,
                  network,
                )
                return {
                  machines,
                  selectedMachine: machines.find(
                    (machine) =>
                      machine.code === refreshed.selectedMachine?.code,
                  ),
                }
              }
            : undefined
        }
        onOpenChange={(open) => {
          if (currentSession.current === session) props.onOpenChange(open)
        }}
        onPurchaseStateChange={onPurchaseStateChange}
        onPurchaseComplete={() => {
          if (currentSession.current === session) props.onPurchaseComplete?.()
        }}
        purchaseExecution={
          network === "solana"
            ? { ...solana, balanceAmount, isBalanceLoading }
            : { ...evm, network: "evm" }
        }
      />
    )
  },
  "GachaOpenPackDialog",
)

type GachaEthConnectionIntent = {
  kind: "login" | "wallet"
  machineCode: string
  price: number
  quantity: number
  instantBuyback: boolean
  userId: string | null
  walletAddress: string
  completed: boolean
}

export const GachaPurchaseDialog = reatomComponent(
  ({
    series,
    open,
    onOpenChange,
    initialMachineCode,
    initialQuantity,
    initialPaymentAsset,
    onPaymentAssetChange,
    initialInstantBuyback,
    autoPurchase,
    onInstantBuybackChange,
    onPurchaseStateChange,
    onRefreshMachinesBeforePurchase,
    onPurchaseComplete,
    purchaseExecution,
  }: GachaOpenPackDialogProps & {
    purchaseExecution: GachaPurchaseExecution
  }) => {
    const { ready, authenticated, user } = usePrivy()
    const { isOpen: isPrivyModalOpen } = useModalStatus()
    const {
      ready: walletsReady,
      walletAddress,
      execution,
      balanceAmount,
      isBalanceLoading,
    } = purchaseExecution
    const { t } = useLingui()
    const paymentTokenSymbol = purchaseExecution.tokenSymbol ?? "USDG"
    const ethPaymentSupported =
      purchaseExecution.supportsEthPayment ??
      Boolean(execution?.quoteEthPayment)
    const paymentAsset = useMemo(
      () => atom<GachaPaymentAsset>("USDG", "gacha.purchase.paymentAsset"),
      [],
    )
    const setPaymentAsset = paymentAsset.set
    const payWithEth = ethPaymentSupported && paymentAsset() === "ETH"
    const ethOffer = useMemo(
      () => atom<GachaEthPaymentOffer | null>(null, "gacha.purchase.ethOffer"),
      [],
    )
    const setEthOffer = ethOffer.set
    const ethConnection = useMemo(
      () =>
        atom<GachaEthConnectionIntent | null>(
          null,
          "gacha.purchase.ethConnection",
        ),
      [],
    )
    const connectionIntent = ethConnection()
    const { login } = useLogin({
      onComplete: ({ user: connectedUser }) => {
        wrap(() => {
          const intent = ethConnection()
          if (intent?.kind !== "login") return
          if (intent.userId && intent.userId !== connectedUser.id) {
            ethConnection.set(null)
            if (autoPurchase) onOpenChange(false)
            return
          }
          ethConnection.set({
            ...intent,
            userId: connectedUser.id,
            completed: true,
          })
        })()
      },
      onError: wrap(() => {
        if (ethConnection()?.kind !== "login") return
        ethConnection.set(null)
        if (autoPurchase) onOpenChange(false)
      }),
    })
    const { connectWallet } = useConnectWallet({
      onSuccess: ({ wallet }) => {
        wrap(() => {
          const intent = ethConnection()
          if (intent?.kind !== "wallet") return
          if (
            intent.walletAddress &&
            intent.walletAddress.toLowerCase() !== wallet.address.toLowerCase()
          ) {
            ethConnection.set(null)
            if (autoPurchase) onOpenChange(false)
            return
          }
          ethConnection.set({
            ...intent,
            walletAddress: wallet.address,
            completed: true,
          })
        })()
      },
      onError: wrap(() => {
        if (ethConnection()?.kind !== "wallet") return
        ethConnection.set(null)
        if (autoPurchase) onOpenChange(false)
      }),
    })
    const ethRecovery = useMemo(
      () =>
        atom<GachaEthPaymentRecoveryError | null>(
          null,
          "gacha.purchase.ethRecovery",
        ),
      [],
    )
    const setEthRecovery = ethRecovery.set
    const selectUsdgForRetry = () => {
      setPaymentAsset("USDG")
      onPaymentAssetChange?.("USDG")
    }
    const showEthRecovery = (recovery: GachaEthPaymentRecoveryError) => {
      if (recovery.status === "confirmed") {
        setEthRecovery(null)
        selectUsdgForRetry()
        sonnerToast.info(
          t`Your previous ETH transaction is confirmed. Check your ${paymentTokenSymbol} balance and pay with ${paymentTokenSymbol} to use it.`,
        )
        if (autoPurchase) onOpenChange(false)
      } else {
        setEthRecovery(recovery)
      }
    }
    const isEvm = purchaseExecution.network === "evm"
    const pending = purchaseExecution.pending ?? false
    const [quantity, setQuantity] = useState(1)
    const [instantBuyback, setInstantBuyback] = useState(false)
    const [purchaseBusy, setPurchaseBusy] = useState(false)
    const [revealPreparing, setRevealPreparing] = useState(false)
    const [purchaseBusyLabel, setPurchaseBusyLabel] = useState("")
    const [purchaseProgressDots, setPurchaseProgressDots] = useState(".")
    const [purchaseDisclaimerOpen, setPurchaseDisclaimerOpen] = useState(false)
    const [purchaseDisclaimerAgreed, setPurchaseDisclaimerAgreed] =
      useState(false)
    const [purchaseDisclaimerError, setPurchaseDisclaimerError] = useState<
      string | null
    >(null)
    const [pullResults, setPullResults] = useState<GachaPullResultView[]>([])
    const [revealResult, setRevealResult] = useState<GachaPullResult | null>(
      null,
    )
    const [revealPackUrl, setRevealPackUrl] = useState("")
    const [resultExecution, setResultExecution] =
      useState<GachaResultExecution | null>(null)
    const [dialogMachines, setDialogMachines] = useState<
      readonly GachaMachineView[]
    >([])
    const [selectedMachineCode, setSelectedMachineCode] = useState<
      string | null
    >(null)
    const {
      revealOpen,
      revealPlayId,
      resultOpen,
      resultFadeIn,
      beginReveal,
      showResults,
      finishReveal,
      closeResults,
    } = useGachaReveal()
    const selectedMachineButtonRef = useRef<HTMLButtonElement | null>(null)
    const autoPurchaseStartedRef = useRef(false)
    const preserveInstantBuybackOnNextOpenRef = useRef(false)
    const {
      introOpen,
      resetForNewPull,
      markBuybackUsed,
      handleResultDialogClosed,
      handleIntroOpenChange,
      handleActivateInstantBuyback,
    } = useGachaInstantBuybackIntro({
      onActivateInstantBuyback: () => {
        preserveInstantBuybackOnNextOpenRef.current = true
        setInstantBuyback(true)
        onInstantBuybackChange?.(true)
        if (!autoPurchase) {
          onOpenChange(true)
        }
      },
    })
    const sortedMachines = useMemo(() => {
      return series
        ? getSortedSeriesMachines({ ...series, machines: dialogMachines })
        : [...dialogMachines]
    }, [dialogMachines, series])

    useEffect(() => {
      if (!series) return
      if (open && autoPurchaseStartedRef.current) return

      const nextMachines = getSortedSeriesMachines(series)
      autoPurchaseStartedRef.current = false
      setDialogMachines(nextMachines)
      setQuantity(Math.max(1, Math.min(10, initialQuantity ?? 1)))
      setPaymentAsset(initialPaymentAsset ?? "USDG")
      setEthOffer(null)
      setEthRecovery(null)
      setInstantBuyback(
        preserveInstantBuybackOnNextOpenRef.current
          ? true
          : (initialInstantBuyback ?? false),
      )
      preserveInstantBuybackOnNextOpenRef.current = false
      closeResults()
      setPurchaseDisclaimerOpen(false)
      setPurchaseDisclaimerAgreed(false)
      setPurchaseDisclaimerError(null)
      setPullResults([])
      setResultExecution(null)
      setSelectedMachineCode(
        nextMachines.some((machine) => machine.code === initialMachineCode)
          ? (initialMachineCode ?? null)
          : getDefaultMachineCode(nextMachines),
      )
    }, [
      closeResults,
      initialInstantBuyback,
      initialMachineCode,
      initialQuantity,
      initialPaymentAsset,
      open,
      series,
      setPaymentAsset,
      setEthOffer,
      setEthRecovery,
    ])

    useEffect(() => {
      if (open) return
      autoPurchaseStartedRef.current = false
    }, [open])

    const selectedMachine =
      sortedMachines.find((machine) => machine.code === selectedMachineCode) ??
      sortedMachines[0] ??
      null
    const selectedPreview = selectedMachine
      ? getMachinePackImage(selectedMachine)
      : { image: series?.image ?? "", source: "category" as const }
    const selectedUnavailableLabel = selectedMachine
      ? getMachineUnavailableLabel(selectedMachine)
      : ""
    const quantityLimit = selectedMachine
      ? Math.max(1, Math.min(10, selectedMachine.stockTotal || 10))
      : 10
    const totalPrice = formatPackTotal(selectedMachine?.price, quantity)
    const {
      status: disclaimerAcceptanceStatus,
      ensureAcceptanceChecked,
      acceptDisclaimer,
      isAccepting: isDisclaimerAccepting,
    } = useGachaDisclaimerAcceptance()

    useEffect(() => {
      setQuantity((value) => Math.min(value, quantityLimit))
    }, [quantityLimit])

    useEffect(() => {
      if (!open || autoPurchase || resultOpen || !selectedMachineCode) return

      const frame = window.requestAnimationFrame(() => {
        selectedMachineButtonRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        })
      })

      return () => window.cancelAnimationFrame(frame)
    }, [autoPurchase, open, resultOpen, selectedMachineCode])

    useEffect(() => {
      if (!selectedMachine?.turboMode) {
        setInstantBuyback(false)
      }
    }, [selectedMachine?.turboMode])

    const { muted: revealMuted } = useGachaRevealMute()
    const revealCard = useMemo(
      () =>
        revealResult
          ? pullResultToRevealCard(revealResult, { packUrl: revealPackUrl })
          : null,
      [revealPackUrl, revealResult],
    )

    const revealResultImages = useMemo(
      () =>
        pullResults.flatMap((result) => [
          result.frontImageUrl,
          result.backImageUrl,
        ]),
      [pullResults],
    )

    const startReveal = useCallback(
      (result: GachaPullResult, packUrl: string) => {
        setRevealResult(result)
        setRevealPackUrl(packUrl)
        setRevealPreparing(isEvm)
        if (!isEvm && !isWatchGachaMachine(result.machineId)) {
          playGachaRevealWhoosh(revealMuted)
        }
        beginReveal()
      },
      [beginReveal, isEvm, revealMuted],
    )

    useEffect(() => {
      if (!purchaseBusy && !revealPreparing) {
        setPurchaseProgressDots(".")
        return
      }

      const id = window.setInterval(() => {
        setPurchaseProgressDots((dots) => (dots.length >= 3 ? "." : `${dots}.`))
      }, 400)

      return () => window.clearInterval(id)
    }, [purchaseBusy, revealPreparing])

    useEffect(() => {
      if (!autoPurchase) return

      onPurchaseStateChange?.({
        busy: purchaseBusy || revealPreparing,
        label: revealPreparing
          ? t`Loading reveal`
          : purchaseBusy
            ? purchaseBusyLabel || "Buying"
            : "",
        dots:
          purchaseBusy || revealPreparing ? purchaseProgressDots : undefined,
      })
    }, [
      autoPurchase,
      onPurchaseStateChange,
      purchaseBusy,
      purchaseBusyLabel,
      purchaseProgressDots,
      revealPreparing,
      t,
    ])

    const requestEvmWalletConnection = useWrap(async () => {
      if (!walletAddress) {
        connectWallet({ walletChainType: "ethereum-only" })
        return "prompted"
      }
      try {
        const result = await wrap(
          reconnectGachaEvmWallet(user?.linkedAccounts, walletAddress, window),
        )
        if (result === "unlinked") {
          sonnerToast.error(t`Reconnect the EVM wallet linked to your account`)
        } else if (result === "unavailable") {
          sonnerToast.error(
            t`Open or install the wallet linked to ${walletAddress}, then try again`,
          )
        } else if (result === "mismatch") {
          sonnerToast.error(t`Connect ${walletAddress} to purchase this pack`)
        }
        return result
      } catch (caught) {
        const message = getPurchaseErrorMessage(
          caught instanceof Error || typeof caught === "string" ? caught : null,
          isEvm,
        )
        sonnerToast.error(message)
        return "failed"
      }
    }, "GachaPurchaseDialog.requestEvmWalletConnection")

    const checkEthConversion = useWrap(async () => {
      const recovery = ethRecovery()
      if (!recovery || purchaseBusy) return
      if (
        !execution?.checkEthPayment ||
        execution.walletAddress.toLowerCase() !==
          recovery.submission.walletAddress.toLowerCase()
      ) {
        sonnerToast.error(
          t`Reconnect the original conversion wallet to check its status.`,
        )
        return
      }
      setPurchaseBusy(true)
      setPurchaseBusyLabel(t`Checking ETH conversion`)
      try {
        await wrap(execution.checkEthPayment())
        setEthRecovery(null)
        sonnerToast.info(
          t`The conversion is no longer pending. Review a new payment before trying again.`,
        )
        if (autoPurchase) onOpenChange(false)
      } catch (caught) {
        if (caught instanceof GachaEthPaymentRecoveryError)
          showEthRecovery(caught)
        else
          sonnerToast.error(
            t`Could not check the conversion. Check your wallet before retrying.`,
          )
      } finally {
        setPurchaseBusy(false)
        setPurchaseBusyLabel("")
      }
    }, "GachaPurchaseDialog.checkEthConversion")

    const handlePurchase = useWrap(async (offer?: GachaEthPaymentOffer) => {
      if (!selectedMachine || pending || purchaseBusy || revealOpen) return
      const privyId = user?.id

      if (!ready || !walletsReady) {
        sonnerToast.error("Wallets are still loading")
        return
      }

      if (!authenticated) {
        login()
        if (autoPurchase) {
          onOpenChange(false)
        }
        return
      }

      if (!execution) {
        if (isEvm) {
          await wrap(requestEvmWalletConnection())
        } else {
          sonnerToast.error("Connect or create a Solana wallet to start")
        }
        if (autoPurchase) {
          onOpenChange(false)
        }
        return
      }

      if (!privyId) {
        sonnerToast.error("User session is still loading. Please try again.")
        return
      }

      let ethTransactionConfirmed = false
      const explainEthRetry = () => {
        if (ethTransactionConfirmed)
          sonnerToast.info(
            t`ETH transaction confirmed. Check Activity and ${paymentTokenSymbol} in the original wallet before retrying. If ${paymentTokenSymbol} arrived, select ${paymentTokenSymbol} instead of converting ETH again.`,
          )
      }
      try {
        autoPurchaseStartedRef.current = true
        resetForNewPull()
        setPurchaseBusy(true)
        setResultExecution(
          execution.buyback
            ? {
                walletAddress: execution.walletAddress,
                buyback: execution.buyback,
              }
            : null,
        )

        const latestMachineResult = onRefreshMachinesBeforePurchase
          ? await wrap(onRefreshMachinesBeforePurchase())
          : null
        const latestMachines = latestMachineResult?.machines?.length
          ? latestMachineResult.machines
          : sortedMachines
        const latestSelectedMachine =
          latestMachineResult?.selectedMachine ??
          latestMachines.find(
            (machine) => machine.code === selectedMachine.code,
          ) ??
          selectedMachine
        const latestSortedMachines = series
          ? getSortedSeriesMachines({ ...series, machines: latestMachines })
          : [...latestMachines]
        const latestQuantityLimit = Math.max(
          0,
          Math.min(10, latestSelectedMachine.stockTotal),
        )
        const purchaseQuantity = Math.min(quantity, latestQuantityLimit)
        const purchaseTotalPriceAmount =
          Number.isFinite(latestSelectedMachine.price) && purchaseQuantity > 0
            ? latestSelectedMachine.price * purchaseQuantity
            : 0
        const purchaseTotalPrice = formatPackTotal(
          latestSelectedMachine.price,
          purchaseQuantity,
        )

        setDialogMachines(latestSortedMachines)
        setSelectedMachineCode(latestSelectedMachine.code)
        setQuantity(Math.max(1, purchaseQuantity))

        if (latestQuantityLimit <= 0) {
          sonnerToast.error(
            "This pack is sold out. Please choose another pack.",
          )
          if (autoPurchase) {
            onOpenChange(false)
          }
          return
        }

        if (quantity > latestQuantityLimit) {
          sonnerToast.error(
            `Only ${latestQuantityLimit} ${latestQuantityLimit === 1 ? "pack is" : "packs are"} left. Please try again with ${latestQuantityLimit}.`,
          )
          if (autoPurchase) {
            onOpenChange(false)
          }
          return
        }

        execution.validatePurchase({
          machine: latestSelectedMachine,
          quantity: purchaseQuantity,
          instantBuyback,
        })

        if (!isEvm && isBalanceLoading) {
          sonnerToast.error(
            "Checking USDC balance. Please try again in a moment.",
          )
          return
        }

        if (
          !isEvm &&
          balanceAmount !== null &&
          balanceAmount + 0.000001 < purchaseTotalPriceAmount
        ) {
          sonnerToast.error(
            `Insufficient USDC. You need ${formatUsd(purchaseTotalPriceAmount)}, but this wallet has ${formatUsd(balanceAmount)} USDC.`,
          )
          if (autoPurchase) {
            onOpenChange(false)
          }
          return
        }

        if (payWithEth && !offer) {
          if (!execution.quoteEthPayment)
            throw new Error(
              "ETH payments are unavailable for this wallet or network",
            )
          setPurchaseBusyLabel(t`Getting ETH quote`)
          const offer = await wrap(
            execution.quoteEthPayment({
              machine: latestSelectedMachine,
              quantity: purchaseQuantity,
              instantBuyback,
            }),
          )
          setEthRecovery(null)
          setEthOffer(offer)
          return
        }
        if (offer && !payWithEth)
          throw new Error("Payment method changed. Review the purchase again.")
        setPurchaseBusyLabel(
          purchaseQuantity > 1
            ? `Creating pack 1/${purchaseQuantity}`
            : `Buy for ${purchaseTotalPrice}`,
        )

        const outcome = await wrap(
          execution.purchase({
            machine: latestSelectedMachine,
            quantity: purchaseQuantity,
            instantBuyback,
            privyId,
            onProgress: (label) =>
              setPurchaseBusyLabel(
                label === "Confirm ETH conversion"
                  ? t`Confirm ETH conversion`
                  : label === "Confirming ETH conversion"
                    ? t`Confirming ETH conversion`
                    : label,
              ),
            ...(offer
              ? {
                  ethPayment: offer,
                  onEthTransactionConfirmed: () => {
                    ethTransactionConfirmed = true
                    selectUsdgForRetry()
                  },
                }
              : {}),
          }),
        )
        setEthOffer(null)
        if (ethTransactionConfirmed && outcome.pending) {
          sonnerToast.info(
            t`ETH transaction confirmed. Do not convert or pay again for pending packs; check Activity. Unspent ${paymentTokenSymbol} remains in the original wallet.`,
          )
        } else if (
          outcome.errorMessage ||
          outcome.completedPacks < purchaseQuantity
        ) {
          explainEthRetry()
        }
        const {
          results: pullResults,
          turboBuybacks,
          turboBuybackTotal,
          completedPacks,
          cancelled: purchaseCancelledAfterOpenedPacks,
        } = outcome

        setPullResults(pullResults)
        if (pullResults[0]) {
          startReveal(pullResults[0], selectedPreview.image)
        }
        const successMessage = purchaseCancelledAfterOpenedPacks
          ? turboBuybacks > 0
            ? `Purchase partially complete. ${completedPacks} of ${purchaseQuantity} packs opened. ${formatInstantBuybackSummary(turboBuybacks, turboBuybackTotal)}`
            : `Purchase partially complete. ${completedPacks} of ${purchaseQuantity} packs opened.`
          : turboBuybacks > 0
            ? `Purchase complete. ${formatInstantBuybackSummary(turboBuybacks, turboBuybackTotal)}`
            : purchaseQuantity === 1
              ? "Purchase complete. Your pull is ready."
              : `Purchase complete. ${purchaseQuantity} packs opened.`
        if (outcome.cancelledByUser) {
          if (completedPacks > 0) {
            sonnerToast.success(successMessage)
          } else {
            sonnerToast.error("Purchase cancelled.")
          }
          return
        }
        if (outcome.pending) {
          if (outcome.errorMessage) {
            sonnerToast.error(outcome.errorMessage)
            return
          }
          const revealedPending = pullResults.some(
            (result) => result.deliveryPending,
          )
          if (revealedPending && completedPacks === purchaseQuantity) {
            sonnerToast.success(successMessage)
            return
          }
          const notStarted = Math.max(
            0,
            purchaseQuantity - completedPacks - (revealedPending ? 0 : 1),
          )
          sonnerToast.info(
            revealedPending
              ? purchaseQuantity > 1
                ? t`${completedPacks} of ${purchaseQuantity} packs revealed. NFT delivery pending; ${notStarted} not started. Do not pay again.`
                : t`Your card is revealed. NFT delivery is pending. Do not pay again.`
              : purchaseQuantity > 1
                ? t`${completedPacks} of ${purchaseQuantity} packs opened. 1 Issued Pack pending; ${notStarted} not started. Do not pay again.`
                : t`Your Issued Pack is pending. Do not pay again.`,
          )
          return
        }
        if (!pullResults[0]) showResults(false)
        if (outcome.errorMessage) {
          sonnerToast.error(`${successMessage} ${outcome.errorMessage}`)
        } else {
          sonnerToast.success(successMessage)
        }
        if (completedPacks > 0) {
          trackProductEvent("gacha_purchase_completed", {
            packs_opened: completedPacks,
            requested_packs: purchaseQuantity,
            instant_buyback_enabled: instantBuyback,
          })
        }
      } catch (caught) {
        setEthOffer(null)
        explainEthRetry()
        if (caught instanceof GachaEthPaymentRecoveryError) {
          showEthRecovery(caught)
          return
        }
        sonnerToast.error(
          getPurchaseErrorMessage(
            caught instanceof Error || typeof caught === "string"
              ? caught
              : null,
            isEvm,
          ),
        )
        if (autoPurchase) {
          onOpenChange(false)
        }
      } finally {
        setPurchaseBusy(false)
        setPurchaseBusyLabel("")
      }
    }, "GachaPurchaseDialog.handlePurchase")

    const requestPurchaseConfirmation = useWrap(async () => {
      if (!selectedMachine || purchaseBusy || pending || ethConnection()) return

      if (!ready || !walletsReady) {
        sonnerToast.error("Wallets are still loading")
        return
      }

      const connectionIntent = (
        kind: GachaEthConnectionIntent["kind"],
      ): GachaEthConnectionIntent => ({
        kind,
        machineCode: selectedMachine.code,
        price: selectedMachine.price,
        quantity,
        instantBuyback,
        userId: user?.id ?? null,
        walletAddress,
        completed: false,
      })

      if (!authenticated) {
        if (payWithEth) ethConnection.set(connectionIntent("login"))
        login()
        if (autoPurchase && !payWithEth) {
          onOpenChange(false)
        }
        return
      }

      if (!execution) {
        if (isEvm) {
          const intent = payWithEth ? connectionIntent("wallet") : null
          ethConnection.set(intent)
          const result = await wrap(requestEvmWalletConnection())
          if (intent && result !== "prompted") {
            ethConnection.set((current) =>
              current === intent
                ? result === "connected"
                  ? { ...intent, completed: true }
                  : null
                : current,
            )
            if (result !== "connected" && autoPurchase) onOpenChange(false)
          }
        } else {
          sonnerToast.error("Connect or create a Solana wallet to start")
        }
        if (autoPurchase && !payWithEth) {
          onOpenChange(false)
        }
        return
      }

      if (!user?.id) {
        sonnerToast.error("User session is still loading. Please try again.")
        return
      }

      setPurchaseDisclaimerError(null)

      let gateAction = getGachaDisclaimerPurchaseGateAction({
        cachedStatus: disclaimerAcceptanceStatus,
      })

      if (gateAction.action === "purchase") {
        void handlePurchase()
        return
      }

      try {
        const acceptance = await wrap(ensureAcceptanceChecked())
        gateAction = getGachaDisclaimerPurchaseGateAction({
          cachedStatus: disclaimerAcceptanceStatus,
          checkedAccepted: acceptance.accepted,
        })
      } catch (caught) {
        gateAction = getGachaDisclaimerPurchaseGateAction({
          cachedStatus: disclaimerAcceptanceStatus,
          checkErrorMessage: getPurchaseErrorMessage(
            caught instanceof Error || typeof caught === "string"
              ? caught
              : null,
            isEvm,
          ),
        })
      }

      if (gateAction.action === "purchase") {
        void handlePurchase()
        return
      }

      setPurchaseDisclaimerError(gateAction.errorMessage ?? null)
      setPurchaseDisclaimerAgreed(false)
      setPurchaseDisclaimerOpen(true)
    }, "GachaPurchaseDialog.requestPurchaseConfirmation")

    // biome-ignore lint/correctness/useExhaustiveDependencies: wrap captures the callback; dependencies bind the deferred ETH review to the initiating selection.
    useEffect(
      wrap(() => {
        if (!connectionIntent) return
        if (
          !open ||
          !payWithEth ||
          pending ||
          selectedMachine?.code !== connectionIntent.machineCode ||
          selectedMachine?.price !== connectionIntent.price ||
          (autoPurchase &&
            ((initialMachineCode &&
              initialMachineCode !== connectionIntent.machineCode) ||
              (initialQuantity !== undefined &&
                initialQuantity !== connectionIntent.quantity) ||
              (initialPaymentAsset !== undefined &&
                initialPaymentAsset !== "ETH") ||
              (initialInstantBuyback ?? false) !==
                connectionIntent.instantBuyback)) ||
          quantity !== connectionIntent.quantity ||
          instantBuyback !== connectionIntent.instantBuyback ||
          Boolean(selectedUnavailableLabel) ||
          (connectionIntent.userId &&
            user?.id &&
            connectionIntent.userId !== user.id) ||
          (connectionIntent.walletAddress &&
            walletAddress &&
            connectionIntent.walletAddress.toLowerCase() !==
              walletAddress.toLowerCase())
        ) {
          ethConnection.set(null)
          if (autoPurchase) onOpenChange(false)
          return
        }
        if (
          !connectionIntent.completed ||
          !ready ||
          !walletsReady ||
          !authenticated ||
          !user?.id ||
          purchaseBusy ||
          isPrivyModalOpen
        )
          return
        if (!execution && connectionIntent.kind !== "login") return
        if (
          execution &&
          connectionIntent.walletAddress &&
          execution.walletAddress.toLowerCase() !==
            connectionIntent.walletAddress.toLowerCase()
        ) {
          ethConnection.set(null)
          return
        }
        ethConnection.set(null)
        void requestPurchaseConfirmation()
      }),
      [
        authenticated,
        autoPurchase,
        connectionIntent,
        ethConnection,
        execution,
        initialInstantBuyback,
        initialMachineCode,
        initialPaymentAsset,
        initialQuantity,
        instantBuyback,
        isPrivyModalOpen,
        open,
        payWithEth,
        pending,
        purchaseBusy,
        quantity,
        ready,
        requestPurchaseConfirmation,
        selectedMachine,
        selectedUnavailableLabel,
        user?.id,
        walletAddress,
        walletsReady,
      ],
    )

    const handlePurchaseDisclaimerOpenChange = useCallback(
      (nextOpen: boolean) => {
        if (isDisclaimerAccepting && !nextOpen) return

        setPurchaseDisclaimerOpen(nextOpen)
        if (nextOpen) return

        setPurchaseDisclaimerAgreed(false)
        setPurchaseDisclaimerError(null)
        if (autoPurchase && !purchaseBusy) {
          onOpenChange(false)
        }
      },
      [autoPurchase, isDisclaimerAccepting, onOpenChange, purchaseBusy],
    )

    const handlePurchaseDisclaimerConfirm = useWrap(async () => {
      if (!purchaseDisclaimerAgreed || isDisclaimerAccepting) return

      if (!execution) {
        if (isEvm) {
          setPurchaseDisclaimerOpen(false)
          setPurchaseDisclaimerAgreed(false)
          setPurchaseDisclaimerError(null)
          await wrap(requestEvmWalletConnection())
          if (autoPurchase) onOpenChange(false)
        } else {
          setPurchaseDisclaimerError(
            "Connect or create a Solana wallet to start",
          )
        }
        return
      }

      try {
        setPurchaseDisclaimerError(null)
        await wrap(acceptDisclaimer(walletAddress))
        setPurchaseDisclaimerOpen(false)
        setPurchaseDisclaimerAgreed(false)
        void handlePurchase()
      } catch (caught) {
        setPurchaseDisclaimerError(
          getPurchaseErrorMessage(
            caught instanceof Error || typeof caught === "string"
              ? caught
              : null,
            isEvm,
          ),
        )
      }
    }, "GachaPurchaseDialog.handlePurchaseDisclaimerConfirm")

    const handleOpenAnotherPackFromResult = useCallback(
      (result: GachaPullResult) => {
        closeResults()
        setPullResults([])
        setRevealResult(null)
        setRevealPackUrl("")

        if (autoPurchase) {
          onOpenChange(false)
          return
        }

        if (result.machineId) {
          setSelectedMachineCode(result.machineId)
        }
        setQuantity(1)
        onOpenChange(true)
      },
      [autoPurchase, closeResults, onOpenChange],
    )

    // biome-ignore lint/correctness/useExhaustiveDependencies: wrap captures the callback; dependencies scope the authorized auto-purchase.
    useEffect(
      wrap(() => {
        if (!autoPurchase || !open || autoPurchaseStartedRef.current) return
        if (initialMachineCode && selectedMachineCode !== initialMachineCode)
          return
        if (!selectedMachine || Boolean(selectedUnavailableLabel)) return

        autoPurchaseStartedRef.current = true
        requestPurchaseConfirmation()
      }),
      [
        autoPurchase,
        open,
        initialMachineCode,
        requestPurchaseConfirmation,
        selectedMachine,
        selectedMachineCode,
        selectedUnavailableLabel,
      ],
    )

    if (!series) return null

    return (
      <>
        <Dialog
          modal={shouldUseModalGachaPurchaseDialog({ isPrivyModalOpen })}
          open={shouldOpenGachaPurchaseDialog({
            open,
            resultOpen,
            revealOpen,
            autoPurchase: Boolean(autoPurchase),
          })}
          onOpenChange={(nextOpen) => {
            if (!nextOpen && revealOpen) return
            onOpenChange(nextOpen)
          }}
        >
          <GachaDialogContent
            className="flex max-h-[min(88dvh,760px)] max-w-[480px] flex-col gap-0 overflow-hidden p-0"
            preventDismiss={
              (isEvm ? purchaseExecution.signing : purchaseBusy) || revealOpen
            }
          >
            <DialogHeader className="shrink-0 gap-2 space-y-0 p-5 pb-0 text-left">
              <DialogTitle className="font-headline font-normal text-2xl leading-tight tracking-tight text-foreground">
                {getGachaPackTitle(series.name)}
              </DialogTitle>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 px-5 pt-5">
                <PackPreview
                  machine={selectedMachine}
                  image={selectedPreview.image}
                  source={selectedPreview.source}
                  quantity={quantity}
                  preloadMachines={sortedMachines}
                />
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                <div className="grid grid-cols-1 gap-2">
                  {sortedMachines.map((machine) => {
                    const unavailableLabel = getMachineUnavailableLabel(machine)
                    const active = machine.code === selectedMachine?.code
                    const featured = isFeaturedGachaMachine(
                      series.slug,
                      machine.code,
                    )

                    return (
                      <button
                        key={machine.code}
                        ref={active ? selectedMachineButtonRef : undefined}
                        type="button"
                        className={cn(
                          "flex items-center justify-between rounded-md border px-3 py-2.5 text-left transition-colors",
                          active
                            ? "border-brand bg-brand/10"
                            : "border-foreground/10 bg-foreground/[0.02] hover:bg-foreground/[0.06]",
                          unavailableLabel ? "opacity-60" : "",
                        )}
                        onClick={() => setSelectedMachineCode(machine.code)}
                      >
                        <span className="flex min-w-0 flex-col gap-1">
                          <span
                            className={cn(
                              "truncate text-sm",
                              featured ? "text-brand" : "text-foreground",
                            )}
                          >
                            {machine.name}
                          </span>
                          <span className="font-mono text-xs text-foreground/50">
                            {machine.label}
                            {getPackAvailabilityText(machine)}
                          </span>
                        </span>
                        <span
                          className={cn(
                            "font-headline text-base",
                            featured ? "text-brand" : "text-foreground",
                          )}
                        >
                          {machine.priceLabel}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="sticky bottom-0 z-10 flex shrink-0 flex-col gap-4 border-foreground/10 border-t bg-background p-5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-foreground/60">Quantity</span>
                  <div className="flex h-10 items-center overflow-hidden rounded-md border border-foreground/10">
                    <button
                      type="button"
                      className="flex h-full w-10 items-center justify-center text-lg text-foreground transition-colors hover:bg-foreground/10 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={quantity <= 1}
                      onClick={() =>
                        setQuantity((value) => Math.max(1, value - 1))
                      }
                    >
                      -
                    </button>
                    <div className="min-w-10 text-center font-mono text-sm">
                      {quantity}
                    </div>
                    <button
                      type="button"
                      className="flex h-full w-10 items-center justify-center text-lg text-foreground transition-colors hover:bg-foreground/10 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={quantity >= quantityLimit}
                      onClick={() =>
                        setQuantity((value) =>
                          Math.min(quantityLimit, value + 1),
                        )
                      }
                    >
                      +
                    </button>
                  </div>
                </div>

                {ethPaymentSupported && (
                  <GachaPaymentSelector
                    value={paymentAsset()}
                    tokenSymbol={paymentTokenSymbol}
                    onChange={setPaymentAsset}
                    disabled={
                      purchaseBusy ||
                      pending ||
                      !selectedMachine ||
                      Boolean(selectedUnavailableLabel)
                    }
                  />
                )}
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-2">
                  {!isEvm && (
                    <GachaSendGiftButton
                      selectedMachine={selectedMachine}
                      packImage={selectedPreview.image}
                      initialQuantity={quantity}
                      disabled={
                        !selectedMachine ||
                        Boolean(selectedUnavailableLabel) ||
                        purchaseBusy
                      }
                      onGiftSent={onPurchaseComplete}
                    />
                  )}
                  <Button
                    type="button"
                    variant="default"
                    className={gachaCtaButtonRowPrimaryClassName}
                    disabled={
                      !selectedMachine ||
                      Boolean(selectedUnavailableLabel) ||
                      purchaseBusy ||
                      revealOpen ||
                      pending
                    }
                    onClick={requestPurchaseConfirmation}
                  >
                    <GachaProgressLabel
                      label={
                        revealPreparing
                          ? t`Loading reveal`
                          : purchaseBusy
                            ? purchaseBusyLabel || "Buying"
                            : selectedUnavailableLabel ||
                              (!authenticated
                                ? "Login"
                                : payWithEth
                                  ? t`Pay with ETH · ${totalPrice}`
                                  : `Buy for ${totalPrice}`)
                      }
                      dots={
                        purchaseBusy || revealPreparing
                          ? purchaseProgressDots
                          : undefined
                      }
                    />
                  </Button>
                </div>

                {!isEvm && (
                  <div className="flex items-center justify-between gap-3 border-foreground/10 border-t pt-4">
                    <div className="flex min-w-0 items-center gap-1.5 text-sm leading-none">
                      <span className="inline-flex items-center gap-1 whitespace-nowrap text-brand">
                        <span className="font-medium">Instant</span>
                        <span>Buyback</span>
                      </span>
                      <span className="shrink-0 rounded-full border border-brand/30 bg-brand/10 px-2 py-0.5 font-mono text-[0.6875rem] leading-none text-brand">
                        {formatBuybackRatePercent(
                          selectedMachine?.instantBuybackPercent ?? 0,
                        )}
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-brand outline-none ring-offset-background transition-colors hover:opacity-80 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                            aria-label="Instant Buyback details"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="size-full"
                            >
                              <title>Instant Buyback information</title>
                              <path
                                d="M12 4C16.4183 4 20 7.58172 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12C4 7.58172 7.58172 4 12 4ZM12 5.59961C8.46538 5.59961 5.59961 8.46538 5.59961 12C5.59961 15.5346 8.46538 18.4004 12 18.4004C15.5346 18.4004 18.4004 15.5346 18.4004 12C18.4004 8.46538 15.5346 5.59961 12 5.59961ZM12.0664 10.9336C12.6185 10.9338 13.0664 11.3814 13.0664 11.9336V15.2666C13.0664 15.8187 12.6185 16.2664 12.0664 16.2666H11.9336C11.3813 16.2666 10.9336 15.8188 10.9336 15.2666V11.9336C10.9336 11.3813 11.3813 10.9336 11.9336 10.9336H12.0664ZM12.0674 7.7334C12.6193 7.73384 13.0674 8.18138 13.0674 8.7334V8.86719C13.0671 9.41895 12.6191 9.86675 12.0674 9.86719H11.9336C11.3815 9.86719 10.9339 9.41922 10.9336 8.86719V8.7334C10.9336 8.18111 11.3813 7.7334 11.9336 7.7334H12.0674Z"
                                fill="currentColor"
                              />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px] text-left font-normal leading-snug">
                          Instant buyback offer — receive up to{" "}
                          {formatBuybackRatePercent(
                            selectedMachine?.instantBuybackPercent ?? 0,
                          )}{" "}
                          of market value on eligible Common cards. Convert
                          low-tier cards from your pack into credit for your
                          next chase instantly.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Switch
                      checked={instantBuyback}
                      onCheckedChange={setInstantBuyback}
                      disabled={!selectedMachine?.turboMode}
                      aria-label="Instant Buyback"
                      className="shrink-0"
                    />
                  </div>
                )}
              </div>
            </div>
          </GachaDialogContent>
        </Dialog>
        <GachaEthPaymentRecoveryDialog
          recovery={ethRecovery()}
          busy={purchaseBusy}
          progress={purchaseBusyLabel}
          onCheck={() => {
            void checkEthConversion()
          }}
          onCancel={() => {
            if (purchaseBusy) return
            setEthRecovery(null)
            if (autoPurchase) onOpenChange(false)
          }}
          onAcknowledge={() => {
            const recovery = ethRecovery()
            if (!recovery || purchaseBusy) return
            if (
              !execution?.acknowledgeUnknownEthPayment ||
              execution.walletAddress.toLowerCase() !==
                recovery.submission.walletAddress.toLowerCase()
            ) {
              sonnerToast.error(
                t`Reconnect the original conversion wallet before continuing.`,
              )
              return
            }
            try {
              execution.acknowledgeUnknownEthPayment(recovery.submission.id)
              selectUsdgForRetry()
              setEthRecovery(null)
              if (autoPurchase) onOpenChange(false)
            } catch (caught) {
              if (caught instanceof GachaEthPaymentRecoveryError)
                showEthRecovery(caught)
              else
                sonnerToast.error(
                  t`Could not update conversion tracking. Check your wallet before retrying.`,
                )
            }
          }}
        />
        <GachaEthPaymentReview
          offer={ethOffer()}
          machineName={selectedMachine?.name ?? ""}
          busy={purchaseBusy}
          progress={purchaseBusyLabel}
          onConfirm={() => {
            const offer = ethOffer()
            if (offer) void handlePurchase(offer)
          }}
          onRefresh={() => {
            void handlePurchase()
          }}
          onCancel={() => {
            if (purchaseBusy) return
            setEthOffer(null)
            if (autoPurchase) onOpenChange(false)
          }}
        />
        {isEvm && revealOpen
          ? pullResults.map((result) => (
              <GachaEvmRevealBuybackPreload
                key={result.purchaseId ?? result.memo}
                result={result}
              />
            ))
          : null}
        <GachaRevealOverlay
          open={revealOpen}
          waitForMedia={isEvm}
          resultImages={revealResultImages}
          onStart={() => {
            setRevealPreparing(false)
            if (isEvm && !isWatchGachaMachine(revealResult?.machineId ?? "")) {
              playGachaRevealWhoosh(revealMuted)
            }
          }}
          preload={purchaseBusy}
          playId={revealPlayId}
          card={revealOpen ? revealCard : null}
          skipCardReveal={(isEvm ? pullResults.length : quantity) > 1}
          machineCode={revealResult?.machineId ?? selectedMachine?.code}
          openedPackRarity={revealResult?.rarity}
          packCount={isEvm ? pullResults.length : quantity}
          onFade={showResults}
          onComplete={() => {
            setRevealPreparing(false)
            finishReveal()
            onPurchaseComplete?.()
          }}
        />
        <GachaPullResultDialog
          open={open && resultOpen}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              closeResults()
              if (!isEvm) handleResultDialogClosed()
              onOpenChange(false)
            }
          }}
          pullResults={pullResults}
          execution={resultExecution}
          fadeIn={resultFadeIn}
          onBuybackAccepted={markBuybackUsed}
          onOpenAnotherPack={handleOpenAnotherPackFromResult}
        />
        <GachaInstantBuybackIntroDialog
          open={!isEvm && introOpen}
          buybackRatePercent={selectedMachine?.instantBuybackPercent ?? 0}
          onOpenChange={handleIntroOpenChange}
          onActivate={handleActivateInstantBuyback}
        />
        <GachaPurchaseDisclaimerDialog
          open={purchaseDisclaimerOpen}
          onOpenChange={handlePurchaseDisclaimerOpenChange}
          agreed={purchaseDisclaimerAgreed}
          onAgreedChange={setPurchaseDisclaimerAgreed}
          onConfirm={handlePurchaseDisclaimerConfirm}
          isSubmitting={isDisclaimerAccepting}
          errorMessage={purchaseDisclaimerError}
        />
      </>
    )
  },
  "GachaPurchaseDialog",
)

type GachaModalPullResultDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  pullResults: GachaPullResultView[]
  purchaseWalletAddress: string
  purchaseWallet: ConnectedStandardSolanaWallet | null
  fadeIn?: boolean
  onBuybackAccepted?: () => void
  onOpenAnotherPack?: (result: GachaPullResult) => void
}

export const GachaModalPullResultDialog = reatomComponent(
  ({
    purchaseWalletAddress,
    purchaseWallet,
    ...props
  }: GachaModalPullResultDialogProps) => {
    const { execution } = useGachaSolanaExecution({
      purchaseWalletAddress,
      purchaseWallet,
    })
    return <GachaPullResultDialog {...props} execution={execution} />
  },
  "GachaModalPullResultDialog",
)

export const GachaPullResultDialog = reatomComponent(
  ({
    open,
    onOpenChange,
    pullResults,
    execution,
    fadeIn = false,
    onBuybackAccepted,
    onOpenAnotherPack,
  }: Omit<
    GachaModalPullResultDialogProps,
    "purchaseWalletAddress" | "purchaseWallet"
  > & { execution: GachaResultExecution | null }) => {
    const { user } = usePrivy()
    const [visiblePullResults, setVisiblePullResults] =
      useState<GachaPullResultView[]>(pullResults)
    const [previewSide, setPreviewSide] = useState<"front" | "back">("front")
    const [activePullIndex, setActivePullIndex] = useState(0)
    const [detailsOpen, setDetailsOpen] = useState(false)
    const [buybackConfirmOpen, setBuybackConfirmOpen] = useState(false)
    const [buybackBusy, setBuybackBusy] = useState(false)
    const [buybackBusyLabel, setBuybackBusyLabel] = useState("")
    const [buybackProgressDots, setBuybackProgressDots] = useState(".")
    const [buybackDeadlineMs, setBuybackDeadlineMs] = useState<number | null>(
      null,
    )
    const [, setCountdownTick] = useState(0)
    const selectedResult =
      visiblePullResults[activePullIndex] ?? visiblePullResults[0] ?? null
    const activeResult = resolveDeliveredPullResult(selectedResult)
    const totalPulls = visiblePullResults.length
    const hasMultiplePulls = totalPulls > 1
    const previewImageUrl =
      previewSide === "back"
        ? activeResult?.backImageUrl || activeResult?.frontImageUrl || ""
        : activeResult?.frontImageUrl || ""

    useEffect(() => {
      if (!open) {
        setVisiblePullResults([])
        setPreviewSide("front")
        setActivePullIndex(0)
        setDetailsOpen(false)
        setBuybackConfirmOpen(false)
        return
      }

      setVisiblePullResults(pullResults)
      setPreviewSide("front")
      setActivePullIndex(0)
      setDetailsOpen(false)
      setBuybackConfirmOpen(false)
      setBuybackDeadlineMs(Date.now() + BUYBACK_WINDOW_MS)
    }, [open, pullResults])

    useEffect(() => {
      if (!open || buybackDeadlineMs === null) return
      const id = window.setInterval(() => setCountdownTick((n) => n + 1), 1000)
      return () => window.clearInterval(id)
    }, [buybackDeadlineMs, open])

    useEffect(() => {
      if (!buybackBusy) {
        setBuybackProgressDots(".")
        return
      }

      const id = window.setInterval(() => {
        setBuybackProgressDots((dots) => (dots.length >= 3 ? "." : `${dots}.`))
      }, 400)

      return () => window.clearInterval(id)
    }, [buybackBusy])

    const markBoughtBackResult = useCallback(
      (boughtBackResult: GachaPullResult, boughtBackLabel: string) => {
        setVisiblePullResults((results) => {
          return results.map((result, index) => {
            const isBoughtBackResult = boughtBackResult.memo
              ? result.memo === boughtBackResult.memo
              : index === activePullIndex

            return isBoughtBackResult ? { ...result, boughtBackLabel } : result
          })
        })
      },
      [activePullIndex],
    )

    const handleBuyback = useCallback(async () => {
      const result = activeResult

      if (!execution) {
        sonnerToast.error(
          "Connect or create a Solana wallet to accept buyback.",
        )
        return
      }

      if (!result?.nftAddress || !result.memo) {
        sonnerToast.error("This item is not eligible for buyback.")
        return
      }

      const privyId = user?.id
      if (!privyId) {
        sonnerToast.error("User session is still loading. Please try again.")
        return
      }

      try {
        setBuybackConfirmOpen(false)
        setBuybackBusyLabel(`Accept Buyback ${result.buybackOffer ?? ""}`)
        setBuybackBusy(true)

        const boughtBackLabel = await wrap(
          execution.buyback({ result, privyId }),
        )
        if (boughtBackLabel !== null) {
          sonnerToast.success("Buyback offer accepted")
          trackProductEvent("gacha_buyback_completed", {
            rarity: result.rarity ?? "unknown",
          })
          onBuybackAccepted?.()
          markBoughtBackResult(result, boughtBackLabel)
        }
      } catch (caught) {
        sonnerToast.error(getBuybackErrorMessage(caught))
      } finally {
        setBuybackBusy(false)
        setBuybackBusyLabel("")
      }
    }, [
      activeResult,
      markBoughtBackResult,
      onBuybackAccepted,
      execution,
      user?.id,
    ])

    const countdownLabel =
      buybackDeadlineMs !== null
        ? formatBuybackCountdown(buybackDeadlineMs)
        : "-"
    const isBuybackExpired =
      buybackDeadlineMs !== null && buybackDeadlineMs <= Date.now()

    if (!activeResult) return null
    const boughtBackLabel = activeResult.boughtBackLabel

    const handleOpenAnotherPack = () => {
      onOpenAnotherPack?.(activeResult)
      if (!onOpenAnotherPack) {
        onOpenChange(false)
      }
    }

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <GachaResultDialogContent
          fadeIn={fadeIn}
          preventDismiss
          className={cn(
            "left-[50%] top-[50%] flex max-h-[min(92dvh,calc(100dvh-2rem))] w-[min(calc(100vw-2rem),calc(72rem*0.9))] max-w-[calc(72rem*0.9)] translate-x-[-50%] translate-y-[-50%] flex-col gap-0 overflow-hidden p-0",
            "border bg-background shadow-lg sm:rounded-xl",
          )}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-y-contain px-5 py-6 sm:gap-8 sm:px-8 sm:py-8">
            <DialogHeader className="gap-2 space-y-0 pr-14 text-left sm:pr-12">
              <GachaAuthenticityLabel />
              {hasMultiplePulls ? (
                <div className="w-fit rounded-full border border-foreground/10 bg-foreground/[0.04] px-2 py-1 font-mono text-3xs uppercase leading-none text-foreground/60">
                  CARD {activePullIndex + 1} OF {totalPulls}
                </div>
              ) : null}
              <DialogTitle className="font-normal text-2xl leading-tight tracking-tight text-foreground">
                {activeResult.itemName}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Pull result with market value and buyback offer.
              </DialogDescription>
            </DialogHeader>

            <div className="grid w-full grid-cols-1 gap-8 sm:grid-cols-2 sm:items-start sm:gap-10 lg:gap-12">
              <div className="flex flex-col gap-5">
                <GachaZoomableCardPreview
                  image={previewImageUrl}
                  alt={`${activeResult.itemName} ${previewSide} view`}
                />

                <div
                  className="flex w-full justify-center gap-4 overflow-x-auto px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  aria-label="Card view"
                >
                  <ResultPreviewButton
                    active={previewSide === "front"}
                    image={activeResult.frontImageUrl}
                    label="Front view"
                    onClick={() => setPreviewSide("front")}
                  />
                  <ResultPreviewButton
                    active={previewSide === "back"}
                    image={
                      activeResult.backImageUrl || activeResult.frontImageUrl
                    }
                    label="Back view"
                    onClick={() => setPreviewSide("back")}
                  />
                </div>
              </div>

              <div className="flex min-w-0 flex-col gap-6 sm:flex-1">
                {activeResult.chainId &&
                !activeResult.deliveryPending &&
                activeResult.tokenId !== undefined &&
                activeResult.purchaseWalletAddress &&
                activeResult.paymentTokenAddress ? (
                  <GachaEvmBuybackButton
                    key={`${activeResult.chainId}:${activeResult.contractAddress}:${activeResult.tokenId}:${activeResult.purchaseWalletAddress}`}
                    identity={{
                      chainId: activeResult.chainId,
                      contract: activeResult.contractAddress,
                      tokenId: activeResult.tokenId,
                    }}
                    walletAddress={activeResult.purchaseWalletAddress}
                    paymentToken={activeResult.paymentTokenAddress}
                    item={{
                      image: activeResult.frontImageUrl,
                      name: activeResult.itemName,
                      marketValue: activeResult.marketValue,
                      buybackOffer: "—",
                    }}
                    renderResult={(state) => (
                      <>
                        <GachaBuybackResultSummary
                          buybackOffer={state.buybackOffer}
                          marketValue={activeResult.marketValue}
                          offerStatus={
                            boughtBackLabel || state.boughtBackLabel
                              ? undefined
                              : state.offerStatus
                          }
                        />
                        <GachaBuybackResultActions
                          boughtBackLabel={
                            boughtBackLabel || state.boughtBackLabel
                          }
                          buybackOffer={state.buybackOffer}
                          buybackBusy={state.buybackBusy}
                          buybackBusyLabel=""
                          buybackProgressDots={buybackProgressDots}
                          canAcceptBuyback={state.canAcceptBuyback}
                          offerStatus={state.offerStatus}
                          offerSlow={state.offerSlow}
                          onRetryOffer={state.onRetryOffer}
                          countdownLabel={
                            state.eligibilityUntil === undefined
                              ? ""
                              : formatBuybackCountdown(state.eligibilityUntil)
                          }
                          isBuybackExpired={
                            state.eligibilityUntil !== undefined &&
                            state.eligibilityUntil <= Date.now()
                          }
                          onOpenAnotherPack={handleOpenAnotherPack}
                          onRequestBuyback={state.onRequestBuyback}
                        />
                      </>
                    )}
                    onComplete={(label) => {
                      markBoughtBackResult(activeResult, label)
                      onBuybackAccepted?.()
                    }}
                  />
                ) : (
                  <>
                    <GachaBuybackResultSummary
                      buybackOffer={
                        activeResult.chainId ? "—" : activeResult.buybackOffer
                      }
                      marketValue={activeResult.marketValue}
                      offerStatus={
                        activeResult.chainId && !boughtBackLabel
                          ? "preparing"
                          : undefined
                      }
                    />
                    <GachaBuybackResultActions
                      boughtBackLabel={boughtBackLabel}
                      buybackOffer={
                        activeResult.chainId ? "—" : activeResult.buybackOffer
                      }
                      buybackBusy={buybackBusy}
                      buybackBusyLabel={buybackBusyLabel}
                      buybackProgressDots={buybackProgressDots}
                      offerStatus={
                        activeResult.chainId ? "preparing" : undefined
                      }
                      offerSlow={
                        Boolean(activeResult.chainId) &&
                        buybackDeadlineMs !== null &&
                        Date.now() >=
                          buybackDeadlineMs - BUYBACK_WINDOW_MS + 8000
                      }
                      canAcceptBuyback={
                        !activeResult.chainId &&
                        Boolean(activeResult.nftAddress) &&
                        !isBuybackExpired
                      }
                      countdownLabel={countdownLabel}
                      isBuybackExpired={isBuybackExpired}
                      onOpenAnotherPack={handleOpenAnotherPack}
                      onRequestBuyback={() => setBuybackConfirmOpen(true)}
                    />
                    {activeResult.deliveryPending && (
                      <p className="font-mono text-sm text-foreground/60">
                        <Trans>NFT delivery pending</Trans>
                      </p>
                    )}
                  </>
                )}

                <Card className="p-4 font-mono text-sm">
                  <Collapsible
                    className="group/collapsible flex flex-col gap-3"
                    open={detailsOpen}
                    onOpenChange={setDetailsOpen}
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className="flex w-full cursor-pointer items-center justify-between text-left font-headline text-lg outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      >
                        <span>Details</span>
                        <ChevronIcon className="transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <GachaResultDetailsView result={activeResult} />
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              </div>
            </div>
          </div>

          {hasMultiplePulls ? (
            <div className="flex shrink-0 flex-col gap-3 border-foreground/10 border-t bg-background px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <div className="font-mono text-xs uppercase text-foreground/60">
                CARD {activePullIndex + 1} OF {totalPulls}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:w-[360px]">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 min-h-9"
                  disabled={activePullIndex === 0}
                  onClick={() =>
                    setActivePullIndex((index) => {
                      setPreviewSide("front")
                      setDetailsOpen(false)
                      return Math.max(0, index - 1)
                    })
                  }
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 min-h-9"
                  disabled={activePullIndex === totalPulls - 1}
                  onClick={() =>
                    setActivePullIndex((index) => {
                      setPreviewSide("front")
                      setDetailsOpen(false)
                      return Math.min(totalPulls - 1, index + 1)
                    })
                  }
                >
                  Next Card
                </Button>
              </div>
            </div>
          ) : null}
        </GachaResultDialogContent>
        <Dialog open={buybackConfirmOpen} onOpenChange={setBuybackConfirmOpen}>
          <GachaBuybackConfirmationDialog
            result={activeResult}
            buybackBusy={buybackBusy}
            onCancel={() => setBuybackConfirmOpen(false)}
            onConfirm={handleBuyback}
          />
        </Dialog>
      </Dialog>
    )
  },
  "GachaPullResultDialog",
)

const ResultPreviewButton = reatomComponent(
  ({
    active,
    image,
    label,
    onClick,
  }: {
    active: boolean
    image: string
    label: string
    onClick: () => void
  }) => {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className={cn(
          "relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-foreground/10 bg-[#171717] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#FAFF00]",
          active && "border-[#FAFF00] shadow-[inset_0_0_0_1px_#FAFF00]",
        )}
      >
        <img src={image} alt="" className="h-full w-full object-cover" />
      </button>
    )
  },
  "ResultPreviewButton",
)

const CopyableShortAddress = reatomComponent(
  ({ address }: { address: string }) => {
    if (!address) return <span>-</span>

    return (
      <Copy copyValue={address} label="Copy address" withIcon>
        <LiteralShortAddress address={address} />
      </Copy>
    )
  },
  "CopyableShortAddress",
)

const LiteralShortAddress = reatomComponent(
  ({ address }: { address: string }) => {
    if (!address) return <span>-</span>
    if (address.length <= 11) return <span>{address}</span>

    return (
      <span className="inline-flex min-w-0 max-w-full items-baseline">
        <span className="min-w-0 truncate">{address.slice(0, 4)}</span>
        <span aria-hidden="true" className="inline-flex shrink-0">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
        <span className="shrink-0">{address.slice(-4)}</span>
      </span>
    )
  },
  "LiteralShortAddress",
)

const GachaResultDetailsView = reatomComponent(
  ({ result }: { result: GachaPullResult }) => {
    return (
      <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
        <span className="text-nowrap text-foreground/60">Grade</span>
        <span className="min-w-0 justify-self-end text-right">
          {result.grade}
        </span>
        {result.chainId != null && (
          <>
            <span className="text-nowrap text-foreground/60">
              <Trans>Chain ID</Trans>
            </span>
            <span className="min-w-0 justify-self-end text-right">
              {result.chainId}
            </span>
            <span className="text-nowrap text-foreground/60">
              <Trans>Contract</Trans>
            </span>
            <span className="min-w-0 justify-self-end text-right">
              <CopyableShortAddress address={result.contractAddress} />
            </span>
          </>
        )}
        <span className="text-nowrap text-foreground/60">Token ID</span>
        <span className="min-w-0 justify-self-end text-right">
          <CopyableShortAddress
            address={result.tokenId ?? result.nftAddress ?? ""}
          />
        </span>
      </div>
    )
  },
  "GachaResultDetailsView",
)

const GachaBuybackConfirmationDialog = reatomComponent(
  ({
    result,
    buybackBusy,
    onCancel,
    onConfirm,
  }: {
    result: GachaPullResult
    buybackBusy: boolean
    onCancel: () => void
    onConfirm: () => void
  }) => {
    return (
      <GachaDialogContent
        className="z-[230] max-w-[440px] gap-5 p-5"
        preventDismiss={buybackBusy}
      >
        <GachaBuybackConfirmationBody
          item={{
            image: result.frontImageUrl,
            name: result.itemName,
            collectionName: result.collectionName,
            marketValue: result.marketValue,
            buybackOffer: result.buybackOffer,
            buybackRatePercent: result.buybackRatePercent,
          }}
          buybackBusy={buybackBusy}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      </GachaDialogContent>
    )
  },
  "GachaBuybackConfirmationDialog",
)

const PackPreview = reatomComponent(
  ({
    machine,
    image,
    source,
    quantity,
    preloadMachines,
  }: {
    machine: GachaMachineView | null
    image: string
    source: "api" | "category" | "config"
    quantity: number
    preloadMachines: readonly GachaMachineView[]
  }) => {
    const visiblePacks = Array.from({ length: Math.min(quantity, 5) })
    const center = (visiblePacks.length - 1) / 2

    if (machine && isWatchGachaMachine(machine.code)) {
      return (
        <div className="relative flex h-[190px] w-full items-center justify-center overflow-hidden sm:h-[280px] [@media_(min-width:640px)_and_(max-height:760px)]:h-[220px]">
          <GachaMachineMedia
            machine={machine}
            fit="contain"
            preloadMachines={preloadMachines}
          />
        </div>
      )
    }

    if (source === "api") {
      return (
        <div className="relative flex h-[190px] w-full items-center justify-center overflow-hidden sm:h-[280px] [@media_(min-width:640px)_and_(max-height:760px)]:h-[220px]">
          {visiblePacks.map((_, index) => {
            const offset = index - center
            const isFront = index === visiblePacks.length - 1

            return (
              <img
                key={`${image}-${index}`}
                src={image}
                alt=""
                className="absolute h-[160px] w-[220px] object-contain drop-shadow-[0_16px_28px_rgba(0,0,0,0.38)] transition-all duration-200 sm:h-[220px] sm:w-[280px] [@media_(min-width:640px)_and_(max-height:760px)]:h-[175px] [@media_(min-width:640px)_and_(max-height:760px)]:w-[235px]"
                style={{
                  opacity: isFront ? 1 : 0.78,
                  transform: `translateX(${offset * 34}px) rotate(${offset * 3}deg)`,
                  transformOrigin: "50% 85%",
                  zIndex: index + 1,
                }}
              />
            )
          })}
        </div>
      )
    }

    return (
      <div className="relative flex h-[190px] w-full items-center justify-center overflow-hidden sm:h-[280px] [@media_(min-width:640px)_and_(max-height:760px)]:h-[220px]">
        {visiblePacks.map((_, index) => {
          const offset = index - center
          const isFront = index === visiblePacks.length - 1

          return (
            <img
              key={`${image}-${index}`}
              src={image}
              alt=""
              className="absolute h-[170px] w-[230px] object-contain drop-shadow-[0_16px_28px_rgba(0,0,0,0.38)] transition-all duration-200 sm:h-[240px] sm:w-[300px] [@media_(min-width:640px)_and_(max-height:760px)]:h-[190px] [@media_(min-width:640px)_and_(max-height:760px)]:w-[245px]"
              style={{
                opacity: isFront ? 1 : 0.78,
                transform: `translateX(${offset * 34}px) rotate(${offset * 3}deg)`,
                transformOrigin: "50% 85%",
                zIndex: index + 1,
              }}
            />
          )
        })}
      </div>
    )
  },
  "PackPreview",
)

function getGachaPackTitle(seriesName: string) {
  return seriesName.endsWith("Gacha Pack")
    ? seriesName
    : `${seriesName} Gacha Pack`
}

function formatPackTotal(price: number | undefined, quantity: number) {
  const amount = Number.isFinite(price) ? Number(price) : 0

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount * quantity)
}
